import { useEffect, useRef } from 'react';

/**
 * Хук для debounce эффекта
 * @param {Function} effect - функция, которая будет вызвана после задержки
 * @param {Array} deps - зависимости эффекта
 * @param {number} delay - задержка в миллисекундах (по умолчанию 500)
 * @param {Object} options - дополнительные опции
 * @param {boolean} options.leading - выполнить сразу при первом вызове
 * @param {boolean} options.trailing - выполнить после задержки
 */
export function useDebounceEffect(effect, deps, delay = 500, options = {}) {
  const { leading = false, trailing = true } = options;
  const effectRef = useRef(effect);
  const timeoutRef = useRef(null);
  const mountedRef = useRef(true);
  const isLeadingCalledRef = useRef(false);

  // Обновляем ссылку на эффект при изменении
  useEffect(() => {
    effectRef.current = effect;
  }, [effect]);

  // Очистка при размонтировании
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Если компонент не смонтирован, выходим
    if (!mountedRef.current) return;

    // Очищаем предыдущий таймер
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Leading edge - выполнить сразу
    if (leading && !isLeadingCalledRef.current) {
      isLeadingCalledRef.current = true;
      effectRef.current();
      return;
    }

    // Trailing edge - выполнить после задержки
    if (trailing) {
      timeoutRef.current = setTimeout(() => {
        if (mountedRef.current) {
          effectRef.current();
          isLeadingCalledRef.current = false;
        }
      }, delay);
    }

    // Очистка таймера при изменении зависимостей
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [...deps, delay, leading, trailing]);
}
